export const ORG_FLEX_STORAGE_DATA_TYPES = {
    COMPLAINT_DRAFT: 'complaint_draft',
    INCIDENT_DRAFT: 'incident_draft',
    USER_SETTINGS: 'user_settings',
} as const;

// Record-scoped draft keys: the record ID is embedded in the dataType so the
// (orgId, userId, dataType) unique constraint yields one private draft per
// user per control activity/test without a schema change.
export const CONTROL_ACTIVITY_DRAFT_PREFIX = 'control_activity_draft:';
export const CONTROL_TEST_DRAFT_PREFIX = 'control_test_draft:';

export type ControlActivityDraftDataType = `${typeof CONTROL_ACTIVITY_DRAFT_PREFIX}${string}`;
export type ControlTestDraftDataType = `${typeof CONTROL_TEST_DRAFT_PREFIX}${string}`;

/**
 * CUR-644 phase 7: idempotency fence for a queued register CSV import. One row
 * per import run (`register_import_run:<importRunId>`), written in the same
 * transaction as the imported records so a BullMQ redelivery of an already
 * committed run is a no-op instead of a duplicate import.
 *
 * Deliberately NOT part of {@link OrgFlexStorageDataType}: this is server-only
 * bookkeeping written straight through Prisma, never a user draft served by the
 * draft DAL (`isRenderableDraftDataType` returns false for it). Unlike drafts
 * these rows carry a real `expiresAt` and are swept by `dbCleanUp`.
 */
export const REGISTER_IMPORT_RUN_MARKER_PREFIX = 'register_import_run:';

export function buildRegisterImportRunMarkerDataType(importRunId: string): string {
    return `${REGISTER_IMPORT_RUN_MARKER_PREFIX}${importRunId}`;
}

/**
 * A refused run's terminal outcome, recorded so the status poll can still
 * report it after BullMQ has evicted the job.
 *
 * A SEPARATE key from the commit marker above, deliberately: that marker is the
 * idempotency fence, and every read of it ("has this run already imported?")
 * must stay a plain existence check. Sharing one key would make a recorded
 * refusal look like a committed run to a redelivery, which would then report
 * success for rows that were never written.
 */
export const REGISTER_IMPORT_RUN_REFUSAL_PREFIX = 'register_import_refusal:';

export function buildRegisterImportRunRefusalDataType(importRunId: string): string {
    return `${REGISTER_IMPORT_RUN_REFUSAL_PREFIX}${importRunId}`;
}

/**
 * CUR-1825: the record an incident/complaint create form was opened from. A draft
 * saved from one source must never hydrate a form opened from another, or the
 * stale draft blocks the AI prefill for the new source (the prefill engine only
 * fills empty fields). The source is encoded in the `dataType`, like the control
 * drafts, so the (orgId, userId, dataType) unique index yields one draft per user
 * per source without a schema change: `incident_draft` (no source),
 * `incident_draft:<sourceType>:<id>`, `complaint_draft:<sourceType>:<id>`.
 */
export const FORM_DRAFT_SOURCE_TYPES = [
    'complianceAuditFinding',
    'messageClassification',
    'complaint',
    'incident',
    'claim',
] as const;

export type FormDraftSourceType = (typeof FORM_DRAFT_SOURCE_TYPES)[number];

/**
 * Sources the incident form can be opened from (its peer is a complaint). The
 * order is the precedence {@link resolveFormDraftSource} applies.
 */
export const INCIDENT_DRAFT_SOURCE_TYPES = [
    'complianceAuditFinding',
    'messageClassification',
    'complaint',
] as const satisfies readonly FormDraftSourceType[];

/**
 * Sources the complaint form can be opened from (its peer is an incident). The
 * order is the precedence {@link resolveFormDraftSource} applies.
 */
export const COMPLAINT_DRAFT_SOURCE_TYPES = [
    'complianceAuditFinding',
    'messageClassification',
    'incident',
    'claim',
] as const satisfies readonly FormDraftSourceType[];

export type IncidentDraftSourceType = (typeof INCIDENT_DRAFT_SOURCE_TYPES)[number];
export type ComplaintDraftSourceType = (typeof COMPLAINT_DRAFT_SOURCE_TYPES)[number];

export type FormDraftSource<T extends FormDraftSourceType = FormDraftSourceType> = { type: T; id: bigint };
export type IncidentDraftSource = FormDraftSource<IncidentDraftSourceType>;
export type ComplaintDraftSource = FormDraftSource<ComplaintDraftSourceType>;

export type FormDraftBase =
    | typeof ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT
    | typeof ORG_FLEX_STORAGE_DATA_TYPES.COMPLAINT_DRAFT;

type FormDraftSourceTypeFor<B extends FormDraftBase> = B extends typeof ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT
    ? IncidentDraftSourceType
    : ComplaintDraftSourceType;

export type FormDraftDataType = FormDraftBase | `${FormDraftBase}:${FormDraftSourceType}:${string}`;

const FORM_DRAFT_SOURCE_TYPES_BY_BASE: { readonly [B in FormDraftBase]: readonly FormDraftSourceTypeFor<B>[] } = {
    [ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT]: INCIDENT_DRAFT_SOURCE_TYPES,
    [ORG_FLEX_STORAGE_DATA_TYPES.COMPLAINT_DRAFT]: COMPLAINT_DRAFT_SOURCE_TYPES,
};

/** Both form draft bases, for filters that match a base key and every source-scoped key of it. */
export const FORM_DRAFT_BASES = [
    ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT,
    ORG_FLEX_STORAGE_DATA_TYPES.COMPLAINT_DRAFT,
] as const satisfies readonly FormDraftBase[];

/** Prefix shared by every source-scoped key of `base` (`incident_draft:`). */
export function formDraftSourcePrefix(base: FormDraftBase): `${FormDraftBase}:` {
    return `${base}:`;
}

export type OrgFlexStorageDataType =
    | (typeof ORG_FLEX_STORAGE_DATA_TYPES)[keyof typeof ORG_FLEX_STORAGE_DATA_TYPES]
    | FormDraftDataType
    | ControlActivityDraftDataType
    | ControlTestDraftDataType;

/**
 * CONSTRAINT: control activity ids are allocated as MAX(id)+1 per org (see
 * server/src/dal/control-activity-id.ts), so a deleted record's id can be
 * recycled by a later create. No delete path for controls/activities exists
 * today; if one is ever added it MUST clear the record's drafts
 * (clearOrgFlexStorageDraftForAllUsers) or a stale never-completed draft could
 * be served into the unrelated record that inherits the id (bounded by the
 * 30-day stale-draft sweep). The same applies to control tests.
 */
export function buildControlActivityDraftDataType(id: bigint): ControlActivityDraftDataType {
    return `${CONTROL_ACTIVITY_DRAFT_PREFIX}${id.toString()}`;
}

export function buildControlTestDraftDataType(id: bigint): ControlTestDraftDataType {
    return `${CONTROL_TEST_DRAFT_PREFIX}${id.toString()}`;
}

/** Largest value a PostgreSQL `bigint` (and therefore any record id) can hold. */
export const MAX_BIGINT_ID = 9_223_372_036_854_775_807n;

/** Only a positive, in-range `bigint` in canonical decimal names a real record. */
function parseRecordIdSuffix(suffix: string): bigint | null {
    if (!/^[1-9]\d{0,18}$/.test(suffix)) return null;

    const id = BigInt(suffix);
    return id <= MAX_BIGINT_ID ? id : null;
}

/**
 * Reads the record id back out of a record-scoped draft `dataType`.
 *
 * `dataType` is free-form text in the database, so anything reading a stored draft key must
 * treat the suffix as untrusted: only a positive, in-range `bigint` names a real destination.
 * Returns `null` for a different prefix or a malformed/out-of-range suffix.
 */
export function parseControlDraftRecordId(dataType: string, prefix: string): bigint | null {
    if (!dataType.startsWith(prefix)) return null;

    return parseRecordIdSuffix(dataType.slice(prefix.length));
}

/** The draft key for `base`, scoped to `source` when the form was opened from one. */
export function buildFormDraftDataType<B extends FormDraftBase>(
    base: B,
    source?: FormDraftSource<FormDraftSourceTypeFor<B>>,
): FormDraftDataType {
    return source ? `${base}:${source.type}:${source.id.toString()}` : base;
}

type ParsedFormDraftKey<B extends FormDraftBase> = {
    base: B;
    source: FormDraftSource<FormDraftSourceTypeFor<B>> | null;
};

export type ParsedFormDraftDataType =
    | ParsedFormDraftKey<typeof ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT>
    | ParsedFormDraftKey<typeof ORG_FLEX_STORAGE_DATA_TYPES.COMPLAINT_DRAFT>;

function parseFormDraftKey<B extends FormDraftBase>(base: B, dataType: string): ParsedFormDraftKey<B> | null {
    if (dataType === base) return { base, source: null };

    const prefix = formDraftSourcePrefix(base);
    if (!dataType.startsWith(prefix)) return null;

    const suffix = dataType.slice(prefix.length);
    const separator = suffix.indexOf(':');
    if (separator < 0) return null;

    const typeSegment = suffix.slice(0, separator);
    const type = FORM_DRAFT_SOURCE_TYPES_BY_BASE[base].find((allowed) => allowed === typeSegment);
    if (type === undefined) return null;

    const id = parseRecordIdSuffix(suffix.slice(separator + 1));
    if (id === null) return null;

    return { base, source: { type, id } };
}

/**
 * Reads an incident/complaint draft `dataType` back into its base and source.
 *
 * `dataType` is free-form text in the database, so the suffix is untrusted (as in
 * {@link parseControlDraftRecordId}): only a known base, a source type that base's
 * form accepts and a positive, in-range `bigint` id name a real draft. Anything
 * else returns `null`.
 */
export function parseFormDraftDataType(dataType: string): ParsedFormDraftDataType | null {
    return (
        parseFormDraftKey(ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT, dataType) ??
        parseFormDraftKey(ORG_FLEX_STORAGE_DATA_TYPES.COMPLAINT_DRAFT, dataType)
    );
}

/** The route search param (or drawer prop) that carries each source's id. */
export const FORM_DRAFT_SOURCE_PARAM_KEYS = {
    complianceAuditFinding: 'complianceAuditFindingId',
    messageClassification: 'messageClassificationId',
    complaint: 'complaintId',
    incident: 'incidentId',
    claim: 'claimId',
} as const satisfies Record<FormDraftSourceType, string>;

type FormDraftSourceParamsFor<T extends FormDraftSourceType> = {
    [K in T as (typeof FORM_DRAFT_SOURCE_PARAM_KEYS)[K]]?: bigint;
};

export type IncidentDraftSourceParams = FormDraftSourceParamsFor<IncidentDraftSourceType>;
export type ComplaintDraftSourceParams = FormDraftSourceParamsFor<ComplaintDraftSourceType>;

/** True for an id {@link parseRecordIdSuffix} reads back, so a resolved source always round-trips. */
function isRecordId(id: bigint): boolean {
    return id > 0n && id <= MAX_BIGINT_ID;
}

/**
 * The one source a form's draft is scoped to when the form is opened with several
 * ids: finding, then message, then the peer record (complaint / incident), then claim.
 *
 * Shared by the routes, the claim-page drawers and the Focus Queue link builder, so
 * the key a form writes is the key its shortcut reopens. An id that is not a
 * positive, in-range `bigint` names no record and is skipped.
 */
export function resolveFormDraftSource(
    form: 'incident',
    params: IncidentDraftSourceParams,
): IncidentDraftSource | undefined;
export function resolveFormDraftSource(
    form: 'complaint',
    params: ComplaintDraftSourceParams,
): ComplaintDraftSource | undefined;
export function resolveFormDraftSource(
    form: 'incident' | 'complaint',
    params: IncidentDraftSourceParams | ComplaintDraftSourceParams,
): FormDraftSource | undefined {
    const ids: Partial<Record<(typeof FORM_DRAFT_SOURCE_PARAM_KEYS)[FormDraftSourceType], bigint>> = params;
    const types: readonly FormDraftSourceType[] =
        form === 'incident' ? INCIDENT_DRAFT_SOURCE_TYPES : COMPLAINT_DRAFT_SOURCE_TYPES;

    for (const type of types) {
        const id = ids[FORM_DRAFT_SOURCE_PARAM_KEYS[type]];
        if (id !== undefined && isRecordId(id)) return { type, id };
    }

    return undefined;
}

/** True when a stored draft row names a destination the app can actually open. */
export function isRenderableDraftDataType(dataType: string): boolean {
    return (
        parseFormDraftDataType(dataType) !== null ||
        parseControlDraftRecordId(dataType, CONTROL_TEST_DRAFT_PREFIX) !== null ||
        parseControlDraftRecordId(dataType, CONTROL_ACTIVITY_DRAFT_PREFIX) !== null
    );
}
